import React, { useCallback, useMemo, useState } from 'react';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import Alert from '@mui/material/Alert';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { useExplainImage } from 'apps/sigma2-ui/src/lib/hooks/useExplainImage';
import { LevelDefinitionButton } from 'apps/sigma2-ui/src/components/LevelDefinitionButton';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { ExplanationBlock } from 'apps/sigma2-ui/src/components/ExplanationBlock';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { PipelineAnalysisRequest } from 'libs/end-to-end/src/custom-viz/PipelineAnalysisTypes';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyFnArgs } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { getDimensionFilterValue } from 'libs/end-to-end/src/progress-page-utils';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { DynamicPipelineAnalysisOutput } from 'libs/end-to-end/src/dynamic-pipeline-analysis';
import { DynamicPipelineAnalysis } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { DynamicPipelineRenderer } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineContext';
import { resolveAnalysisNameForCustomViz } from 'libs/end-to-end/src/construct-body-display-of-custom-viz';

export const getPipelineAnalysisPrimaryFilterSelectionsFromRequest = ({
  request,
}: {
  request: Pick<PipelineAnalysisRequest, 'inputs'>;
}): PrimaryFilterSelection[] => {
  const extras = request.inputs.extras;

  const { dimension } = extras;

  return [
    {
      primaryFilterKey: 'dimension',
      value: dimension,
    },
  ];
};

export const DynamicPipelineAnalysisFullInterface = (props: {
  observation: MyObservation;
  organizationId: string;
  analysisName: string;
}) => {
  const { observation, organizationId, analysisName } = props;

  const context = useCustomViz();

  const shareLinkRawData = useShareLinkSearchParameter();

  const { myUser, userAuthenticatedApiCall } = useUser();

  const { doViz } = context;

  const getCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: {
      primaryFilterSelections: PrimaryFilterSelection[];
      secondaryFilterSelections: SecondaryFilterSelection[];
    }): { customVizRequest: PipelineAnalysisRequest | undefined } => {
      const dimension = getDimensionFilterValue(primaryFilterSelections);

      if (!dimension || !observation?.type) {
        return { customVizRequest: undefined };
      }

      const filters = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections,
      });

      const customVizRequest: PipelineAnalysisRequest = {
        customVizId: 'pipelineAnalysis',
        organizationId,
        observationLocationType: {
          type: 'db-revision',
          databaseRevisionId: observation.type.databaseRevisionId,
          databaseId: observation.type.databaseId,
        },
        inputs: {
          dropdowns: {},
          extras: {
            filters,
            dimension,
            analysisName,
          },
        },
      };

      return {
        customVizRequest,
      };
    },
    [analysisName, observation?.type, organizationId],
  );

  const onQueryProgress = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      isProgrammaticChange,
      isInitialApply,
    }: OnApplyFnArgs) => {
      if (
        isProgrammaticChange ||
        (isInitialApply && shareLinkRawData !== null)
      ) {
        return;
      }

      const { customVizRequest } = getCustomVizRequest({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      if (!customVizRequest) {
        return;
      }

      doViz(customVizRequest);
    },
    [doViz, getCustomVizRequest, shareLinkRawData],
  );

  const analytics = useAnalytics();

  const getElementToDownload = useCallback(
    // this callback is only used if downloadContainerRef is not null so we
    // can safely assert it is not null at this point
    () => {
      analytics.trackEvent({
        eventName: 'Chart Downloaded',
        eventProperties: {
          type: 'Pipeline Analysis',
        },
      });
      return imageRef!.current;
    },
    [analytics],
  );

  const filterPayload = useFilters({
    feature: 'employeeLifecycle',
    focusArea: null,
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    onApply: onQueryProgress,
    getElementToDownload,
    getCustomVizRequest,
    analysisName,
  });

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (
        !request ||
        !observation ||
        request.customVizId !== 'pipelineAnalysis'
      ) {
        return;
      }

      const primaryFilterSelections =
        getPipelineAnalysisPrimaryFilterSelectionsFromRequest({
          request,
        });

      const requestAnalysisName = resolveAnalysisNameForCustomViz(request);

      if (analysisName !== requestAnalysisName) {
        return;
      }

      const secondaryFilterSelections =
        getSecondaryFilterSelectionsFromCustomVizFormat({
          feature: 'employeeLifecycle',
          focusArea: null,
          customVizFilters: request.inputs.extras.filters,
          focusAreas: observation.focusAreas,
          filterSetDefinitionsWithValues:
            observation.filterSetDefinitionsWithValues,
          dimensionDefinitions: observation.dimensions,
          analysisName,
          pipelineAnalysisConfiguration:
            observation.pipelineAnalysisConfiguration,
        });

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      doViz(request);
    },
    [analysisName, doViz, filterPayload, observation],
  );

  useShareLinkRequest({
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: PipelineAnalysisRequest,
    onLoaded: onShareLinkLoaded,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  const imageRef = React.useRef<HTMLInputElement | null>(null);

  const { customVizRequest: lastCustomVizRequest } = useMemo(
    () =>
      getCustomVizRequest({
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      getCustomVizRequest,
    ],
  );

  return (
    <div ref={imageRef} className={'flex flex-col gap-4'}>
      <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />

      <HorizontalFilterBar items={filterPayload.items} />
      <Card
        className={'bg-white relative min-h-[500px] mb-6 flex'}
        paddingClassName={'p-0'}
      >
        <DynamicPipelineAnalysisCard
          customVizRequest={lastCustomVizRequest}
          result={context.result}
          status={context.status}
        />
      </Card>
    </div>
  );
};

export const DynamicPipelineAnalysisCard = (props: {
  customVizRequest: PipelineAnalysisRequest | undefined;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
}) => {
  const { customVizRequest, result, status } = props;

  const { getDimensionByColumnHeaderValue } = useDimensionConfig();
  const language = useLanguage();

  const {
    data: pipelineAnalysisOutput,
    isLoading,
    hasFailed,
  } = useMemo(() => {
    return parseCustomVizResult({
      result: result,
      status: status,
      ResultZodType: DynamicPipelineAnalysisOutput,
    });
  }, [result, status]);

  const imageRef = React.useRef<HTMLInputElement | null>(null);
  const explainImageHook = useExplainImage({
    autoExplain: pipelineAnalysisOutput !== null,
    ref: imageRef,
    key: JSON.stringify(customVizRequest),
  });

  const { getAllLevelsForDimension } = useDimensionConfig();
  const [selectedLevel, setSelectedLevel] = useState<string>('');

  // Get the display name of the dimension from the result, since we use this in the title of the AI explanation
  const aiExplanationDimension = useMemo(() => {
    if (!pipelineAnalysisOutput) {
      return '';
    }

    const dimensionDefinition = getDimensionByColumnHeaderValue(
      pipelineAnalysisOutput.dimension,
    );

    if (!dimensionDefinition) {
      return '';
    }

    return dimensionDefinition.displayName;
  }, [getDimensionByColumnHeaderValue, pipelineAnalysisOutput]);

  return (
    <>
      {isLoading && (
        <div className="z-cover flex items-center justify-center w-full h-full flex-1 place-self-center flex-1">
          <CustomVizCircularSpinner />
        </div>
      )}
      {!isLoading && hasFailed && (
        <div className="z-cover flex items-center justify-center w-full h-full min-h-96">
          <Alert severity="error">
            There&apos;s not enough data to render, please adjust your filters
          </Alert>
        </div>
      )}
      {pipelineAnalysisOutput && !isLoading && !hasFailed && (
        <>
          {explainImageHook.explanation && (
            <ExplanationBlock
              explanation={explainImageHook.explanation}
              titleTranslatedContent={language.employeeLifecycleAIExplanationHeading(
                {
                  dimension: aiExplanationDimension,
                },
              )}
            />
          )}
          <div className="p-4 w-full">
            <div className="flex flex-wrap whitespace-normal gap-3 pb-4 items-center">
              <S2Typography
                variant="headline-7-alt"
                translatedContent={language.togglePathway()}
              />
              {getAllLevelsForDimension(pipelineAnalysisOutput.dimension).map(
                (level) => (
                  <div key={level.columnValue}>
                    <LevelDefinitionButton
                      disabled={false}
                      selected={selectedLevel === level.columnValue}
                      levelDefinition={level}
                      onSelect={() => {
                        setSelectedLevel(
                          selectedLevel === level.columnValue
                            ? ''
                            : level.columnValue,
                        );
                      }}
                    />
                  </div>
                ),
              )}
            </div>
            <DynamicPipelineRenderer>
              <DynamicPipelineAnalysis
                pipelineAnalysisOutput={pipelineAnalysisOutput}
                selectedLevel={selectedLevel}
              />
            </DynamicPipelineRenderer>
          </div>
        </>
      )}
    </>
  );
};
